import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { PermissionService } from '../../../shared/services/permission.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { WhatsappContactCleanupApiService } from '../services/whatsapp-contact-cleanup-api.service';
import { WhatsappContactCleanupCounts } from '../models/whatsapp.model';

/** Una fila del desglose: etiqueta legible + cantidad. */
interface FilaDesglose {
  etiqueta: string;
  cantidad: number;
}

/**
 * Mantenimiento: borrar de golpe los contactos que WhatsApp creó solo mientras el alta
 * automática estuvo encendida.
 *
 * El flujo es obligatorio —previsualizar, leer el desglose, confirmar— y no se puede saltar:
 * el POST exige el recuento que devolvió la previsualización.
 *
 * La ruta va detrás de `adminGuard` y el ítem del menú se oculta con la misma regla que
 * `/whatsapp/setup` (ver `MenuVisibilityService.isPathAllowed`): los dos endpoints exigen
 * rol Admin o Super Admin, así que ofrecer la pantalla a los demás sólo serviría para
 * regalarles un 403.
 */
@Component({
  selector: 'app-whatsapp-contact-cleanup',
  standalone: true,
  imports: [CommonModule, RouterLink, PageBreadcrumbComponent],
  templateUrl: './whatsapp-contact-cleanup.component.html',
})
export class WhatsappContactCleanupComponent implements OnInit {
  private readonly api = inject(WhatsappContactCleanupApiService);
  private readonly confirm = inject(ConfirmDialogService);
  private readonly toast = inject(ToastService);
  private readonly permissions = inject(PermissionService);
  private readonly auth = inject(AuthService);

  previsualizando = false;
  ejecutando = false;
  errorMessage: string | null = null;

  /** null = todavía no se previsualizó. El botón de ejecutar depende de esto. */
  conteo: WhatsappContactCleanupCounts | null = null;
  /** Momento de la última ejecución, para dejar constancia en pantalla. */
  ejecutadoEn: string | null = null;

  /**
   * La previsualización se pide sola al entrar: es de sólo lectura y sin ella la pantalla
   * no tendría nada que enseñar. Obligar a pulsar un botón sólo para ver el diagnóstico
   * añadía un paso a una pantalla a la que se entra precisamente a mirar la cifra.
   *
   * Se espera a que la matriz de permisos esté cargada. `PermissionService` es default-deny
   * mientras no lo esté, así que disparar sin esperar significaba que en el peor momento
   * —la primera pantalla tras un F5, con el GET de permisos todavía en vuelo— un Admin de
   * verdad se encontraba con «sólo un Admin puede usar esta herramienta» y ninguna cifra.
   * `loaded$` es un BehaviorSubject: si ya está cargada, esto es síncrono.
   */
  ngOnInit(): void {
    this.permissions.loaded$
      .pipe(filter((cargada) => cargada), take(1))
      .subscribe(() => this.previsualizar());
  }

  // ===== Permisos =====
  //
  // Se consultan desde el componente y se funden en un booleano en vez de usar
  // `appDisableWithoutPermission`: esa directiva escribe el atributo `disabled` por su
  // cuenta y aquí los botones ya lo tienen atado a `[disabled]`. Con dos dueños del mismo
  // atributo gana el último que corra, y el botón podía quedar habilitado sin permiso.

  /** Los dos endpoints exigen rol Admin o Super Admin además del permiso del módulo. */
  get esAdmin(): boolean {
    const rol = this.auth.getCurrentUser()?.roleName ?? '';
    return rol === 'Admin' || rol === 'Super Admin';
  }

  get puedePrevisualizar(): boolean {
    return this.esAdmin && this.permissions.has('whatsapp', 'view');
  }

  get puedeEjecutar(): boolean {
    return this.esAdmin && this.permissions.has('whatsapp', 'delete');
  }

  /** Sin contactos que borrar no se ofrece el botón: no habría nada que ejecutar. */
  get hayAlgoQueLimpiar(): boolean {
    return !!this.conteo && this.conteo.contacts > 0;
  }

  // ===== Previsualización =====

  /**
   * @param limpiarError `false` cuando el refresco viene de un 409: el mensaje que explica
   * por qué cambió la cifra tiene que seguir en pantalla, y borrarlo aquí dejaba al usuario
   * con un número nuevo y sin ninguna explicación de por qué no se borró nada.
   */
  previsualizar(limpiarError = true): void {
    if (!this.puedePrevisualizar || this.previsualizando) return;
    this.previsualizando = true;
    if (limpiarError) this.errorMessage = null;

    this.api.preview().subscribe({
      next: (counts) => {
        this.conteo = counts;
        this.previsualizando = false;
      },
      error: (err) => {
        this.previsualizando = false;
        this.conteo = null;
        const msg = this.mensajeDeError(err, 'No se pudo previsualizar la limpieza');
        this.errorMessage = msg;
        this.toast.error({ title: 'Error', message: msg });
      },
    });
  }

  // ===== Ejecución =====

  async ejecutar(): Promise<void> {
    const counts = this.conteo;
    if (!counts || !this.puedeEjecutar || this.ejecutando) return;
    if (counts.contacts <= 0) return;

    const n = counts.contacts;
    const plural = n === 1 ? 'contacto' : 'contactos';
    const arrastra = [
      this.cantidad(counts.deals, 'negocio', 'negocios'),
      this.cantidad(counts.leads, 'lead', 'leads'),
      this.cantidad(counts.tickets, 'ticket', 'tickets'),
      this.cantidad(counts.tasks, 'tarea', 'tareas'),
      this.cantidad(counts.quotes, 'cotización', 'cotizaciones'),
    ].join(', ');
    const ok = await this.confirm.confirmDanger({
      title: `Eliminar ${n} ${plural} de WhatsApp`,
      message:
        `Se van a eliminar ${n} ${plural} y todo lo que cuelgue sólo de ellos (${arrastra}). ` +
        `Las ${counts.preservedConversations} conversaciones y sus ${counts.preservedMessages} mensajes ` +
        'de WhatsApp se conservan: los hilos siguen en la bandeja identificados por el número. ' +
        'Esta acción no se puede deshacer.',
      // El rótulo por defecto de confirmDanger es «Eliminar»: aquí es literal —se borran
      // contactos— pero se le pone el número para que nadie pulse sin saber cuántos.
      confirmText: `Eliminar ${n} ${plural}`,
      cancelText: 'Cancelar',
    });
    if (!ok) return;

    this.ejecutando = true;
    this.errorMessage = null;

    this.api.execute({ expectedContacts: n }).subscribe({
      next: (res) => {
        this.ejecutando = false;
        this.ejecutadoEn = res.executedAt;
        const borrados = res.counts.contacts;
        this.toast.success({
          title: 'Limpieza completada',
          message:
            `Se eliminaron ${borrados} ${borrados === 1 ? 'contacto' : 'contactos'} de WhatsApp. ` +
            `Las ${res.counts.preservedConversations} conversaciones y sus ` +
            `${res.counts.preservedMessages} mensajes se conservan.`,
        });
        // Se vuelve a previsualizar para que la pantalla muestre el estado real (0) en vez
        // de la cifra vieja, que ya no existe.
        this.previsualizar();
      },
      error: (err) => {
        this.ejecutando = false;
        if (err instanceof HttpErrorResponse && err.status === 409) {
          // El conjunto cambió: entraron mensajes nuevos entre la previsualización y el
          // borrado. Reintentar con el mismo número volvería a fallar, así que se refresca
          // la cifra y se le cuenta al usuario con el mensaje que ya redactó el backend.
          const msg = this.mensajeDeError(
            err,
            'El conjunto cambió desde la previsualización. Volvé a previsualizar antes de borrar.'
          );
          this.errorMessage = msg;
          this.toast.warning({ title: 'La cifra cambió', message: msg });
          this.previsualizar(false);
          return;
        }
        if (err instanceof HttpErrorResponse && err.status === 403) {
          const msg =
            'No tenés permiso para ejecutar esta limpieza. Sólo un Admin o Super Admin puede hacerlo.';
          this.errorMessage = msg;
          this.toast.error({ title: 'Error', message: msg });
          return;
        }
        const msg = this.mensajeDeError(err, 'No se pudo ejecutar la limpieza');
        this.errorMessage = msg;
        this.toast.error({ title: 'Error', message: msg });
      },
    });
  }

  // ===== Desglose para la vista =====
  //
  // Se separan en tres grupos porque mezclarlos sería engañoso: hay registros que
  // desaparecen, vínculos que se retiran de registros que SOBREVIVEN, y datos de WhatsApp
  // que no se tocan.

  /**
   * Registros que desaparecen con los contactos.
   *
   * Se listan TODAS las categorías, también las que dan cero. En una pantalla de borrado
   * irreversible una fila ausente es ambigua —¿es cero, o esta pantalla no lo mira?— y la
   * pregunta que trae aquí al usuario suele ser justamente «¿me llevo por delante alguna
   * cotización?». Las que están en cero se pintan atenuadas para que no compitan.
   */
  get seEliminan(): FilaDesglose[] {
    const c = this.conteo;
    if (!c) return [];
    return [
      { etiqueta: 'Negocios', cantidad: c.deals },
      { etiqueta: 'Leads', cantidad: c.leads },
      { etiqueta: 'Tickets', cantidad: c.tickets },
      { etiqueta: 'Tareas', cantidad: c.tasks },
      { etiqueta: 'Cotizaciones', cantidad: c.quotes },
      { etiqueta: 'Aprobaciones de cotización', cantidad: c.quoteApprovals },
      { etiqueta: 'Valores de atributos personalizados', cantidad: c.contactCustomAttributes },
      { etiqueta: 'Envíos de formularios', cantidad: c.formSubmissions },
      { etiqueta: 'Destinatarios de campañas', cantidad: c.campaignRecipients },
      { etiqueta: 'Destinatarios manuales de campañas', cantidad: c.campaignManualRecipients },
      { etiqueta: 'Exclusiones de campañas', cantidad: c.campaignExcludedContacts },
    ];
  }

  /** Vínculos que se retiran: el registro del otro lado se queda. */
  get seDesvinculan(): FilaDesglose[] {
    const c = this.conteo;
    if (!c) return [];
    return [
      { etiqueta: 'Negocios compartidos que pierden el vínculo', cantidad: c.dealContactLinks },
      { etiqueta: 'Tareas compartidas que pierden el vínculo', cantidad: c.taskContactLinks },
      { etiqueta: 'Vínculos con empresas', cantidad: c.contactCompanyLinks },
      { etiqueta: 'Pertenencias a segmentos', cantidad: c.contactSegmentMembers },
    ];
  }

  /** Suma de lo que desaparece, para el encabezado del bloque. */
  get totalEliminado(): number {
    return this.seEliminan.reduce((acc, f) => acc + f.cantidad, 0);
  }

  get totalDesvinculado(): number {
    return this.seDesvinculan.reduce((acc, f) => acc + f.cantidad, 0);
  }

  /** «1 tarea» / «3 tareas»: el diálogo de confirmación lo lee una persona, no un log. */
  private cantidad(n: number, singular: string, plural: string): string {
    return `${n} ${n === 1 ? singular : plural}`;
  }

  private mensajeDeError(err: unknown, fallback: string): string {
    if (err instanceof HttpErrorResponse) {
      const body: any = err.error;
      if (typeof body === 'string' && body.trim()) return body;
      if (body?.message) return body.message;
      if (body?.error) return body.error;
      return `${err.status} ${err.statusText || fallback}`;
    }
    return fallback;
  }
}
